import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Slider from 'react-slick';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connect } from 'react-redux';
import { flow } from 'lodash-es';

import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { isEmptyCreativeConfig, transformCreativeConfig } from 'site-modules/shared/components/native-ad/utils/utils';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { numberWithPluralText } from 'site-modules/shared/utils/string';
import { getCorrelatorTs } from 'client/engagement-handlers/helper';
import {
  getSiteServedAdPath,
  withSiteServedAdWrapper,
} from 'site-modules/shared/components/native-ad/site-served-ad-wrapper/site-served-ad-wrapper';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import {
  AdComponentDefaultProps,
  AdComponentPropTypes,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import {
  EasListingCreativeConfigDefaultProps,
  EasListingCreativeConfigPropTypes,
  NativeAdsCreativeConfigModel,
} from 'client/data/models/native-ads-creative-config';
import { DEALER_CLICKOUT_CUSTOM } from 'site-modules/shared/constants/ads';
import { EAS_LISTING_AD, SRP_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { DEAL_TYPE_ICON, TEXT_COLOR } from 'site-modules/shared/components/deal/deal-constants';
import { DEFAULT_COLOR } from 'site-modules/shared/components/inventory/constants/usurp-inventory-card';
import { ColorInfo } from 'site-modules/shared/components/inventory/color-info/color-info';
import { vdpLinkBuilder } from 'site-modules/shared/utils/vdp-link-constructor';
import { logger } from 'client/utils/logger/logger';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { getM2ForSaleUrl } from 'site-modules/shared/utils/inventory/get-m2-for-sale-url';

import 'site-modules/shared/components/usurp-inventory-card/pricing-details-srp.scss';
import './eas-listing-usurp-ad.scss';

const CTA = 'View Dealer Offer';

const border = '1px solid #e6e6e6';
const Style = {
  imgContainer: {
    height: '200px',
    borderBottom: border,
  },
};
const colorClassName = 'col-auto col mb-0_5';
const NEW_CONDITION = 'NEW';

export function getIndexByPosition(position, numberOfVINs) {
  return (position - 1) % numberOfVINs;
}

export function getClickOutUrl({ isAds11285Enabled, clickOutUrl, make, model, year, vin: configVin }) {
  if (isAds11285Enabled) {
    const vin = configVin || clickOutUrl.match(/eas_vin=(\w+)/)?.[1];
    // vdpLinkBuilder throws error if vin is undefined
    if (vin) {
      try {
        return vdpLinkBuilder({ make, model, year, vin });
      } catch (err) {
        logger.error(err);
      }
    }
    logger.info({ make, model, year, vin, clickOutUrl });
  }
  return clickOutUrl;
}

function AtGlanceItem({ icon, iconTitle, text }) {
  return (
    <li className="size-14 mb-0_5 col-12">
      <span title={iconTitle} className={classnames(icon, 'd-inline-block size-12 me-0_5')} />
      {text}
    </li>
  );
}

AtGlanceItem.propTypes = {
  icon: PropTypes.node.isRequired,
  iconTitle: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function EasListingUsurpAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = EasListingCreativeConfigDefaultProps,
  position,
  ivtClassName,
  easListingAdClassname = undefined,
  showImageByPosition = false,
  isAds11285Enabled = false,
}) {
  const { siteServedAdsUpdater, creativeConfigData } = creativeConfig;
  const [isIntersecting, disconnect] = useOnScreen(adRef, { rootMargin: '0px' }, true, siteServedAdsUpdater);
  const { cddId, ctucId, dealerName, vehicles } = creativeConfigData;
  const slider = useRef();

  function clickHandler() {
    adClickTrackingListener({
      eventTrackingId: DEALER_CLICKOUT_CUSTOM,
      customLineItemId: cddId,
      creativeConfigId: ctucId,
    });
  }

  useEffect(() => {
    if (isIntersecting) {
      adLoadTrackingListener(null, CTA, getCorrelatorTs(), cddId, { creative_config_id: ctucId });
      disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, siteServedAdsUpdater]);

  const nativeStyle = EAS_LISTING_AD.NATIVE_STYLE;

  function clickDots() {
    slider.current.slickPause();
  }

  function appendDots(dots) {
    return (
      <div>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <ul className="slick-dots" onClick={clickDots}>
          {dots}
        </ul>
      </div>
    );
  }

  const vehiclesToRender =
    showImageByPosition && !!vehicles?.length ? [vehicles[getIndexByPosition(position, vehicles.length)]] : vehicles;

  return (
    <li className={classnames('mb-0_75 mb-md-1_5 col-12 col-md-6', ivtClassName, easListingAdClassname)}>
      <div
        className="eas-srp-native-usurp-ad usurp-inventory-card h-100 w-100 rounded-12 bg-white overflow-hidden"
        ref={adRef}
        data-tracking-parent={nativeStyle}
        data-viewability={`${nativeStyle}-${position}`}
      >
        {adTrackers}
        <Slider
          ref={slider}
          className="h-100"
          dots
          dotsClass="meatballs pos-a center-x bottom-0 mb-0_5"
          appendDots={appendDots}
          arrows={false}
          autoplay
          swipe
        >
          {vehiclesToRender?.map(
            ({
              clickOutUrl,
              vehicle_image: vehicleImage,
              price,
              vin,
              year,
              make,
              model,
              bodyStyle,
              trim,
              mileage,
              edmundsDealRating,
              condition,
              exteriorColor,
              exteriorColorRgb,
              interiorColor,
              interiorColorRgb,
            }) => (
              <a
                key={clickOutUrl}
                href={getClickOutUrl({ isAds11285Enabled, clickOutUrl, make, model, year, vin })}
                target="_blank"
                className="site-served-ad-link no-focus pos-r h-100 d-flex flex-column text-decoration-none"
                rel="noopener noreferrer"
                onClick={clickHandler}
              >
                <span
                  className="pos-a ad-label text-white small px-0_5"
                  style={{ right: '8px', top: '8px', zIndex: 1 }}
                >
                  Sponsored
                </span>
                <figure className="pos-r m-0 overflow-hidden border-bottom" style={Style.imgContainer}>
                  <img src={getM2ForSaleUrl(vehicleImage)} alt="" loading="lazy" className="pos-a center-xy w-100" />
                </figure>
                <div className="p-1">
                  <Row className="pb-0_75 mb-1" style={{ borderBottom: border }}>
                    <Col>
                      <h2 className="size-16 text-blue-30">
                        {year} {make} {model}
                      </h2>
                      <div className="medium">
                        {bodyStyle} {trim}
                      </div>
                    </Col>
                    {price && (
                      <Col xs={5} className="text-end" style={{ borderLeft: border }}>
                        <div className="heading-3">{price.split('.')[0]}</div>
                        {!!edmundsDealRating && (
                          <div className="pricing-details">
                            <i
                              className={classnames(
                                'align-middle me-0_75',
                                DEAL_TYPE_ICON[edmundsDealRating],
                                TEXT_COLOR[edmundsDealRating]
                              )}
                              style={{ width: '1em', height: '1em', display: 'inline-block' }}
                              aria-hidden
                            />
                            <span className="fw-bold text-gray-darkest">{edmundsDealRating.toLowerCase()} price</span>
                          </div>
                        )}
                      </Col>
                    )}
                  </Row>
                  <ul className="row list-unstyled text-cool-gray-10 mb-1_25" aria-label="At a glance">
                    {condition === NEW_CONDITION && (
                      <Fragment>
                        {!!exteriorColor && exteriorColor !== DEFAULT_COLOR && (
                          <li className={colorClassName}>
                            <ColorInfo
                              color={{ name: exteriorColor, rgb: exteriorColorRgb }}
                              colorName={exteriorColor}
                              colorSide="Exterior"
                              label={exteriorColor}
                            />
                          </li>
                        )}
                        {!!interiorColor && interiorColor !== DEFAULT_COLOR && (
                          <li className={colorClassName}>
                            <ColorInfo
                              color={{ name: interiorColor, rgb: interiorColorRgb }}
                              colorName={interiorColor}
                              colorSide="Interior"
                              label={interiorColor}
                            />
                          </li>
                        )}
                      </Fragment>
                    )}
                    {!!Number(mileage) && (
                      <AtGlanceItem
                        icon="icon-meter"
                        iconTitle="vehicle mileage"
                        text={numberWithPluralText(Number(mileage), 'mile', true)}
                      />
                    )}
                    <AtGlanceItem icon="icon-location" iconTitle="dealer name" text={dealerName} />
                  </ul>
                </div>
                <div className="px-1 mt-auto" style={{ marginBottom: '47px' }}>
                  <div
                    className={classnames(
                      'w-100 btn text-nowrap py-0_5 px-0_75 rounded-8 text-transform-none size-16 btn-blue-50 fw-medium'
                    )}
                  >
                    {CTA}
                    <span className="icon-open_in_new size-12 ms-0_25" aria-hidden />
                  </div>
                </div>
              </a>
            )
          )}
        </Slider>
        <Experiment name="ads-11285" showDefault>
          <Recipe name="ctrl" isDefault />
          <Recipe name="chall" />
        </Experiment>
      </div>
    </li>
  );
}

EasListingUsurpAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: EasListingCreativeConfigPropTypes,
  easListingAdClassname: PropTypes.string,
  showImageByPosition: PropTypes.bool,
  isAds11285Enabled: PropTypes.bool,
};

const mapStateToProps = state => ({
  mobile: state.mobile,
  isAds11285Enabled:
    ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: 'ads-11285',
      defaultVal: 'ctrl',
    }) === 'chal',
});

const SiteServedAdWrapper = connect(mapStateToProps)(
  withSiteServedAdWrapper(siteServedAdTrackingWrapper(EasListingUsurpAdUI))
);

export function EasListingUsurpAd({
  position = '1',
  showImageByPosition = false,
  mobile = AdComponentDefaultProps.mobile,
  slotRenderEndListener = AdComponentDefaultProps.slotRenderEndListener,
  placeholderComponent = AdComponentDefaultProps.placeholderComponent,
  placeholderComponentProps = AdComponentDefaultProps.placeholderComponentProps,
  customTargeting = AdComponentDefaultProps.customTargeting,
  vehicle = AdComponentDefaultProps.vehicle,
  currentVehicle = AdComponentDefaultProps.currentVehicle,
  dependedOnCurrentVehicle = AdComponentDefaultProps.dependedOnCurrentVehicle,
  wrapperClass = AdComponentDefaultProps.wrapperClass,
  siteServedAdsEnabled = AdComponentDefaultProps.siteServedAdsEnabled,
  breakpoints = AdComponentDefaultProps.breakpoints,
  isIvt = AdComponentDefaultProps.isIvt,
  ivtClassName = AdComponentDefaultProps.ivtClassName,
  ...restProps
}) {
  return (
    <SiteServedAdWrapper
      position={position}
      showImageByPosition={showImageByPosition}
      mobile={mobile}
      slotRenderEndListener={slotRenderEndListener}
      placeholderComponent={placeholderComponent}
      placeholderComponentProps={placeholderComponentProps}
      customTargeting={customTargeting}
      vehicle={vehicle}
      currentVehicle={currentVehicle}
      dependedOnCurrentVehicle={dependedOnCurrentVehicle}
      wrapperClass={wrapperClass}
      siteServedAdsEnabled={siteServedAdsEnabled}
      breakpoints={breakpoints}
      isIvt={isIvt}
      ivtClassName={ivtClassName}
      {...restProps}
      adType={EAS_LISTING_AD}
      wrapperTag="li"
    />
  );
}

EasListingUsurpAd.propTypes = {
  ...AdComponentPropTypes,
  position: PropTypes.string,
  easListingAdClassname: PropTypes.string,
  showImageByPosition: PropTypes.bool,
};

function EasListingUsurpAdAsFallBackUi({ isEmptySrp, ...restProps }) {
  return <EasListingUsurpAdUI {...restProps} showImageByPosition={isEmptySrp} />;
}

const SiteServedAdWrapperWithFallback = flow(
  component =>
    connectToModel(component, {
      isEmptySrp: bindToPath(
        props => getSiteServedAdPath({ ...props, adType: SRP_AD }),
        NativeAdsCreativeConfigModel,
        data => isEmptyCreativeConfig(transformCreativeConfig(data))
      ),
    }),
  siteServedAdTrackingWrapper,
  withSiteServedAdWrapper,
  connect(mapStateToProps)
)(EasListingUsurpAdAsFallBackUi);

export function EasListingUsurpAdAsFallBack({
  position = '1',
  mobile = AdComponentDefaultProps.mobile,
  slotRenderEndListener = AdComponentDefaultProps.slotRenderEndListener,
  placeholderComponent = AdComponentDefaultProps.placeholderComponent,
  placeholderComponentProps = AdComponentDefaultProps.placeholderComponentProps,
  customTargeting = AdComponentDefaultProps.customTargeting,
  vehicle = AdComponentDefaultProps.vehicle,
  currentVehicle = AdComponentDefaultProps.currentVehicle,
  dependedOnCurrentVehicle = AdComponentDefaultProps.dependedOnCurrentVehicle,
  wrapperClass = AdComponentDefaultProps.wrapperClass,
  siteServedAdsEnabled = AdComponentDefaultProps.siteServedAdsEnabled,
  breakpoints = AdComponentDefaultProps.breakpoints,
  isIvt = AdComponentDefaultProps.isIvt,
  ivtClassName = AdComponentDefaultProps.ivtClassName,
  ...restProps
}) {
  return (
    <SiteServedAdWrapperWithFallback
      position={position}
      mobile={mobile}
      slotRenderEndListener={slotRenderEndListener}
      placeholderComponent={placeholderComponent}
      placeholderComponentProps={placeholderComponentProps}
      customTargeting={customTargeting}
      vehicle={vehicle}
      currentVehicle={currentVehicle}
      dependedOnCurrentVehicle={dependedOnCurrentVehicle}
      wrapperClass={wrapperClass}
      siteServedAdsEnabled={siteServedAdsEnabled}
      breakpoints={breakpoints}
      isIvt={isIvt}
      ivtClassName={ivtClassName}
      {...restProps}
      adType={EAS_LISTING_AD}
      wrapperTag="li"
    />
  );
}

EasListingUsurpAdAsFallBack.propTypes = {
  ...AdComponentPropTypes,
  position: PropTypes.string,
  easListingAdClassname: PropTypes.string,
};
